// Query hooks powering the multi-org UX: org switcher, members management,
// invitations (org-side and public accept flow). Kept in its own module to
// avoid further inflating queries.ts and to keep the cache-invalidation
// patterns (see front/CLAUDE.md) close together.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import type { Permission } from './schema'
import type { MemberKind } from '../lib/member-kinds'
import { useAuth } from '../contexts/AuthContext'

// --- Shared types -----------------------------------------------------------

export interface Role {
    uid: string
    code: string
    name: Record<string, string>
    description?: Record<string, string>
    permissions?: Permission[]
    is_builtin?: boolean
}

export interface OrgMembershipSummary {
    uid: string
    code: string
    name: string
    is_home: boolean
    role?: Role
    /**
     * The caller's effective organization-wide permission set in this org
     * (spec 2026-09-04-02). This is the single source PermissionsProvider
     * reads — there is deliberately no second endpoint for it.
     */
    permissions?: Permission[]
    /** The caller's membership kind in this org (spec 2026-10-06-04). */
    kind?: MemberKind
    joined_at: string
    picture?: { document_uid?: string } | null
}

export interface MyOrganizations {
    data: OrgMembershipSummary[]
    default_org_code: string
}

export interface Member {
    user: {
        uid: string
        name?: string | null
        email?: string | null
        picture?: { document_uid?: string } | null
    }
    role?: Role
    /** staff, resident or provider (spec 2026-10-06-04). */
    kind?: MemberKind
    joined_at: string
    last_login_at?: string | null
    is_self: boolean
    is_sole_owner: boolean
    /** Sessions the member holds open in this organization. */
    active_sessions?: number
    /** Kinds of second factor the member enrolled (spec 2026-10-01-03). */
    mfa_factors?: string[]
}

export interface PaginatedMembers {
    data: Member[]
    page: number
    limit: number
    total: number
    total_pages: number
}

export interface Invitation {
    uid: string
    email: string
    status: 'pending' | 'accepted' | 'revoked' | 'expired'
    expires_at: string
    accepted_at?: string | null
    revoked_at?: string | null
    created_at: string
    updated_at: string
    role?: Role
    /** The kind the invitee joins with (spec 2026-10-06-04). */
    kind?: MemberKind
}

export interface InvitationPreview {
    uid: string
    email: string
    status: string
    org_code: string
    org_name: string
    inviter_name?: string
    inviter_email?: string
    role_code: string
    role_name?: Record<string, string>
    expires_at: string
    existing_user: boolean
    requires_password: boolean
    /** The kind the invitee joins with: residents and providers sign in as
     * their contact and need no password (spec 2026-10-06-06). */
    kind?: MemberKind
}

// --- /users/me/organizations -----------------------------------------------

export function useMyOrganizations() {
    // Gate on the access token, not merely on `isAuthenticated`: on a cold load
    // the provider hydrates its state from localStorage, and a query fired
    // before setAuthToken() ran answers 401 — react-query then caches that
    // failure and every consumer (the org switcher, and since spec
    // 2026-09-04-02 the whole permission set) is stuck empty for the session.
    // Keying the query on the token also refetches it the moment a login or a
    // refresh hands out a new one.
    const { accessToken } = useAuth()
    return useQuery({
        queryKey: ['my-organizations', accessToken],
        enabled: Boolean(accessToken),
        queryFn: async (): Promise<MyOrganizations> => {
            const response = await (
                apiClient as unknown as {
                    GET: (
                        path: string
                    ) => Promise<{ data?: MyOrganizations; error?: unknown }>
                }
            ).GET('/api/v1/users/me/organizations')
            if (!response.data) {
                throw new Error('Failed to load my organizations')
            }
            return response.data
        },
    })
}

// --- /organizations/{orgCode}/members --------------------------------------

export interface MembersFilters {
    page?: number
    limit?: number
    q?: string
    role_code?: string
    kind?: MemberKind
}

/** One page of members, for a hook or a live search (a picker's keystrokes). */
export async function fetchMembers(orgCode: string, filters?: MembersFilters): Promise<PaginatedMembers> {
    const query: Record<string, string> = {}
    if (filters?.page) query.page = filters.page.toString()
    if (filters?.limit) query.limit = filters.limit.toString()
    if (filters?.q) query.q = filters.q
    if (filters?.role_code) query.role_code = filters.role_code
    if (filters?.kind) query.kind = filters.kind

    const response = await (
        apiClient as unknown as {
            GET: (
                path: string,
                opts: {
                    params: {
                        path: { orgCode: string }
                        query?: Record<string, string>
                    }
                }
            ) => Promise<{ data?: PaginatedMembers; error?: unknown }>
        }
    ).GET('/api/v1/organizations/{orgCode}/members', {
        params: { path: { orgCode }, query },
    })
    if (!response.data) {
        throw new Error('Failed to load members')
    }
    return response.data
}

export function useMembers(orgCode: string, filters?: MembersFilters) {
    return useQuery({
        queryKey: ['members', orgCode, filters],
        enabled: !!orgCode,
        queryFn: () => fetchMembers(orgCode, filters),
    })
}

export function useMemberMutations(orgCode: string) {
    const qc = useQueryClient()
    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['members', orgCode] })
        qc.invalidateQueries({ queryKey: ['my-organizations'] })
    }

    const updateRole = useMutation({
        mutationFn: async (args: { userUid: string; roleCode?: string; kind?: MemberKind }) => {
            const response = await (
                apiClient as unknown as {
                    PATCH: (
                        path: string,
                        opts: {
                            params: { path: { orgCode: string; userUid: string } }
                            body: { role_code?: string; kind?: MemberKind }
                        }
                    ) => Promise<{ data?: Member; error?: unknown }>
                }
            ).PATCH(
                '/api/v1/organizations/{orgCode}/members/{userUid}',
                {
                    params: { path: { orgCode, userUid: args.userUid } },
                    body: {
                        ...(args.roleCode ? { role_code: args.roleCode } : {}),
                        ...(args.kind ? { kind: args.kind } : {}),
                    },
                }
            )
            if (!response.data) {
                // The API's {title, code} body, so the caller can show why
                // (422 role_not_allowed_for_kind, 403 permission_escalation...).
                throw new Error(response.error ? JSON.stringify(response.error) : 'Failed to update member')
            }
            return response.data
        },
        onSuccess: invalidate,
    })

    const removeMember = useMutation({
        mutationFn: async (userUid: string) => {
            const response = await (
                apiClient as unknown as {
                    DELETE: (
                        path: string,
                        opts: {
                            params: { path: { orgCode: string; userUid: string } }
                        }
                    ) => Promise<{ error?: unknown }>
                }
            ).DELETE(
                '/api/v1/organizations/{orgCode}/members/{userUid}',
                { params: { path: { orgCode, userUid } } }
            )
            if (response.error) {
                throw new Error(JSON.stringify(response.error))
            }
        },
        onSuccess: invalidate,
    })

    const transferOwnership = useMutation({
        mutationFn: async (userUid: string) => {
            const response = await (
                apiClient as unknown as {
                    POST: (
                        path: string,
                        opts: {
                            params: { path: { orgCode: string; userUid: string } }
                        }
                    ) => Promise<{ error?: unknown }>
                }
            ).POST(
                '/api/v1/organizations/{orgCode}/members/{userUid}/transfer-ownership',
                { params: { path: { orgCode, userUid } } }
            )
            if (response.error) {
                throw new Error(JSON.stringify(response.error))
            }
        },
        onSuccess: invalidate,
    })

    return { updateRole, removeMember, transferOwnership }
}

// --- /organizations/{orgCode}/invitations ----------------------------------

export function useInvitations(
    orgCode: string,
    status?: Invitation['status']
) {
    return useQuery({
        queryKey: ['invitations', orgCode, status ?? 'all'],
        enabled: !!orgCode,
        queryFn: async (): Promise<Invitation[]> => {
            const query: Record<string, string> = {}
            if (status) query.status = status
            const response = await (
                apiClient as unknown as {
                    GET: (
                        path: string,
                        opts: {
                            params: {
                                path: { orgCode: string }
                                query?: Record<string, string>
                            }
                        }
                    ) => Promise<{ data?: Invitation[]; error?: unknown }>
                }
            ).GET('/api/v1/organizations/{orgCode}/invitations', {
                params: { path: { orgCode }, query },
            })
            if (!response.data) {
                throw new Error('Failed to load invitations')
            }
            return response.data
        },
    })
}

export function useInvitationMutations(orgCode: string) {
    const qc = useQueryClient()
    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['invitations', orgCode] })
        qc.invalidateQueries({ queryKey: ['members', orgCode] })
    }

    const create = useMutation({
        mutationFn: async (args: {
            email: string
            role_code: string
            kind: MemberKind
            expires_in_days?: number
        }) => {
            const response = await (
                apiClient as unknown as {
                    POST: (
                        path: string,
                        opts: {
                            params: { path: { orgCode: string } }
                            body: typeof args
                        }
                    ) => Promise<{ data?: Invitation; error?: unknown }>
                }
            ).POST('/api/v1/organizations/{orgCode}/invitations', {
                params: { path: { orgCode } },
                body: args,
            })
            if (!response.data) {
                // The API's {title, code} body, so the caller can show why
                // (422 role_not_allowed_for_kind, 403 permission_escalation...).
                throw new Error(response.error ? JSON.stringify(response.error) : 'Failed to create invitation')
            }
            return response.data
        },
        onSuccess: invalidate,
    })

    const revoke = useMutation({
        mutationFn: async (invitationUid: string) => {
            const response = await (
                apiClient as unknown as {
                    DELETE: (
                        path: string,
                        opts: {
                            params: {
                                path: { orgCode: string; invitationUid: string }
                            }
                        }
                    ) => Promise<{ error?: unknown }>
                }
            ).DELETE(
                '/api/v1/organizations/{orgCode}/invitations/{invitationUid}',
                { params: { path: { orgCode, invitationUid } } }
            )
            if (response.error) {
                throw new Error(JSON.stringify(response.error))
            }
        },
        onSuccess: invalidate,
    })

    const resend = useMutation({
        mutationFn: async (invitationUid: string) => {
            const response = await (
                apiClient as unknown as {
                    POST: (
                        path: string,
                        opts: {
                            params: {
                                path: { orgCode: string; invitationUid: string }
                            }
                        }
                    ) => Promise<{ data?: Invitation; error?: unknown }>
                }
            ).POST(
                '/api/v1/organizations/{orgCode}/invitations/{invitationUid}/resend',
                { params: { path: { orgCode, invitationUid } } }
            )
            if (!response.data) {
                throw new Error('Failed to resend invitation')
            }
            return response.data
        },
        onSuccess: invalidate,
    })

    return { create, revoke, resend }
}

// --- Public invitation endpoints (no auth) ---------------------------------

export function usePublicInvitation(token: string | undefined) {
    return useQuery({
        queryKey: ['invitation-preview', token],
        enabled: !!token,
        retry: false,
        queryFn: async (): Promise<InvitationPreview> => {
            const response = await (
                apiClient as unknown as {
                    GET: (
                        path: string,
                        opts: { params: { path: { token: string } } }
                    ) => Promise<{ data?: InvitationPreview; error?: unknown }>
                }
            ).GET('/api/v1/invitations/{token}', {
                params: { path: { token: token! } },
            })
            if (!response.data) {
                throw new Error(JSON.stringify(response.error ?? 'Failed to load invitation'))
            }
            return response.data
        },
    })
}

export interface AcceptInvitationResponse {
    accepted: boolean
    organization?: { code: string; name: string }
    user?: { uid: string; email?: string | null; name?: string | null }
    created_user?: boolean
    attached_existing?: boolean
}

export function useAcceptInvitation() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (args: {
            token: string
            password?: string
            name?: string
        }): Promise<AcceptInvitationResponse> => {
            const response = await (
                apiClient as unknown as {
                    POST: (
                        path: string,
                        opts: {
                            params: { path: { token: string } }
                            body: { password?: string; name?: string }
                        }
                    ) => Promise<{ data?: AcceptInvitationResponse; error?: unknown }>
                }
            ).POST('/api/v1/invitations/{token}/accept', {
                params: { path: { token: args.token } },
                body: { password: args.password, name: args.name },
            })
            if (!response.data) {
                throw new Error(JSON.stringify(response.error ?? 'Failed to accept invitation'))
            }
            return response.data
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['my-organizations'] })
        },
    })
}
